import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";

import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import { useI18n } from "Hooks";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { EmptyState } from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";

import { getOrganizationId, getUserType } from "modules/CommonSelectors";

import { getUserListEnhancer } from "Platform/routes/MessagingV2/modules/MessagingEnhancers";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import {
  isInitiateMessageEnabled,
  CHAT_STATUS_ENUM,
  isMessagingRevempEnabled,
  isPaidConnectModuleForStaff,
} from "MessagingV2/modules/Utils";

import classes from "./UsersPopover.scss";
import classNames from "classnames";
import UserTypeList from "../UserTypeList";
import { withChatOptions } from "Platform/routes/MessagingV2/providers/ChatOptionsProvider";

const styles = {
  emptyStateSubTitleStyle: {
    width: "340px",
  },
};

const staffUserListSelector = ({ userParams, ...rest }) => {
  return { staffParams: userParams, ...rest };
};

const studentUserListSelector = ({ userParams, ...rest }) => {
  return { studentParams: userParams, ...rest };
};

const parentUserListSelector = ({ userParams, ...rest }) => {
  return { parentParams: userParams, ...rest };
};

const UsersPopoverOverlay = props => {
  const {
    searchText,
    studentParams = {},
    staffParams = {},
    parentParams = {},
    onUserClick,
    userTypeListContainerClassName,
    isPopOver = false,
  } = props;
  const { t } = useI18n(["common"]);

  const hideStaffList = _.isEmpty(staffParams["userNodeList"]); // todo add checks for logged usertypes
  const hideStudentList = _.isEmpty(studentParams["userNodeList"]);
  const hideParentList = _.isEmpty(parentParams["userNodeList"]);

  const isEmptyResult = hideStaffList && hideStudentList && hideParentList;

  //  illustration: NoSearchInFilterIllustration,
  //     title: t("common:noResultHeading"),
  //     subtitle:

  // const
  if (isEmptyResult) {
    return !isPopOver ? (
      <div className={classes.emptyStateIllustrationContainer}>
        <EmptyState
          illustration={NoSearchInFilterIllustration}
          title={
            !_.isEmpty(searchText)
              ? t("common:search_results_not_found")
              : t("common:no_label_found", {
                  label: t("common:lowercase", {
                    text: t("common:members"),
                  }),
                })
          }
          subtitle={
            !_.isEmpty(searchText)
              ? t("common:please_try_a_different_keyword")
              : t("common:there_are_no_members_to_start_a_conversation")
          }
          subtitleStyle={styles.emptyStateSubTitleStyle}
          dsVersion="2.0"
        />
      </div>
    ) : (
      <div className={classes.emptyStateContainer}>
        {t("common:no_user_found_for_label", {
          label: searchText,
        })}
      </div>
    );
  }

  const content = (
    <>
      {!hideStaffList ? (
        <UserTypeList
          headerTitle={t("common:staff_members_plural")}
          searchText={searchText}
          onUserClick={onUserClick}
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...staffParams}
        />
      ) : null}
      {!hideStudentList && !hideStaffList ? (
        <div className={classes.dividerV2} />
      ) : null}
      {/* todo remove and replace with css */}
      {!hideStudentList ? (
        <UserTypeList
          headerTitle={t("common:student_plural")}
          searchText={searchText}
          onUserClick={onUserClick}
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...studentParams}
        />
      ) : null}
      {(!hideStaffList || !hideStudentList) && !hideParentList ? (
        <div className={classes.dividerV2} />
      ) : null}
      {/* todo remove and replace with css */}
      {!hideParentList ? (
        <UserTypeList
          headerTitle={t("common:family_member_plural")}
          searchText={searchText}
          onUserClick={onUserClick}
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...parentParams}
        />
      ) : null}
    </>
  );

  return isPopOver ? (
    content
  ) : (
    <div className={classes.userListsContainer}>{content}</div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userId = _.get(ownProps, "activeUserId");
  return {
    isData: true,
    isLoading: false,
    userId: userId,
    organizationId: getOrganizationId(state),
  };
};

const getVariables = props => {
  const curriculumProgramIds = _.get(props, "curriculumProgramIds");
  return {
    userStatus: "ACTIVE",
    chatStatus: isInitiateMessageEnabled()
      ? CHAT_STATUS_ENUM.ALL
      : CHAT_STATUS_ENUM.INITIATED,
    includeInitiatedChatUsers: false,
    ...(!_.isEmpty(curriculumProgramIds) ? { curriculumProgramIds } : {}),
  };
};

const getVariablesForParent = props => {
  const includePaidFamily = _.get(props, "includePaidFamily");

  return !_.isUndefined(includePaidFamily)
    ? {
        includePaidUsersOnly: includePaidFamily,
        ...getVariables(props),
      }
    : getVariables(props);
};

const UsersPopoverOverlayWithLoader = compose(
  withChatOptions(),
  connect(mapStateToProps),
  getUserListEnhancer({
    userType: STAFF,
    selector: staffUserListSelector,
    getVariables,
    skip: () => !isPaidConnectModuleForStaff(),
  }),
  getUserListEnhancer({
    userType: STUDENT,
    selector: studentUserListSelector,
    getVariables,
    skip: () =>
      !isPaidConnectModuleForStaff() ||
      _.isEqual(getUserType(), PARENT) ||
      _.isEqual(getUserType(), STUDENT),
  }),
  getUserListEnhancer({
    userType: PARENT,
    selector: parentUserListSelector,
    getVariables: getVariablesForParent,
    skip: () =>
      _.isEqual(getUserType(), PARENT) || _.isEqual(getUserType(), STUDENT),
  }),
  withLoader
)(UsersPopoverOverlay);

const UsersPopover = props => {
  const {
    containerRef,
    searchText,
    onUserClick,
    isPopOver,
    userTypeListContainerClassName,
    containerClassName,
    excludedUserIds,
    overlayContainerStyle,
    includePaidUsersOnly = false,
    includePaidFamily,
    curriculumProgramIds,
  } = props;
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const overlayContainerClasses = classNames(
    containerClassName,
    classes.contentContainer
  );

  const isOpen = _.size(searchText) > 0;
  return isPopOver ? (
    <Popover
      isOpen={isOpen}
      positions={["bottom"]}
      reposition={false}
      containerClassName={classes.popoverContainer}
      containerParent={containerRef?.current}
      content={
        <div
          className={
            isUIRevempEnabled
              ? classes.overlayContainerV2
              : classes.overlayContainer
          }
          style={overlayContainerStyle}
        >
          <UsersPopoverOverlayWithLoader
            searchText={searchText}
            onUserClick={onUserClick}
            style={overlayContainerStyle}
            excludedUserIds={excludedUserIds}
            includePaidUsersOnly={includePaidUsersOnly}
            includePaidFamily={includePaidFamily}
            curriculumProgramIds={curriculumProgramIds}
            isPopOver
          />
        </div>
      }
    >
      <div />
    </Popover>
  ) : (
    <div className={overlayContainerClasses} style={overlayContainerStyle}>
      <UsersPopoverOverlayWithLoader
        searchText={searchText}
        onUserClick={onUserClick}
        userTypeListContainerClassName={userTypeListContainerClassName}
        includePaidUsersOnly={includePaidUsersOnly}
        includePaidFamily={includePaidFamily}
        curriculumProgramIds={curriculumProgramIds}
      />
    </div>
  );
};

UsersPopover.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  searchText: PropTypes.string.isRequired,
  onUserClick: PropTypes.func.isRequired,
  overlayContainerStyle: PropTypes.object,
};

export default UsersPopover;
